import React, { useEffect, useRef, useState } from 'react';
import { ChevronRightIcon, ClipboardIcon, CheckIcon } from '@heroicons/react/24/outline';
import SlideOverModalLayout from '../modals/SlideOverModalLayout';
import { Avatar } from '../Avatar';
import { ChatTab } from '../patient-files/ChatTab';
import { ChatModeToggle } from '../conversations/ChatModeToggle';
import {
    ConversationAssignment,
    ConversationReadState
} from '../conversations/ConversationActions';
import { RowMenu, type RowMenuItem } from '../billing/RowMenu';
import { StatusPill } from '../patient-files/overview/shared';
import {
    PatientChatProvider,
    useChatApiVersion,
    CASE_LINKS_BRAND,
    DM_LOGO,
    caseLinksManufacturer,
    type CaseLinksLogo
} from '@odo/shared-ui';
import { getChatService } from '../../services/chatService';
import { usePatientConversationActions } from '../../hooks/usePatientConversationActions';
import { useChatStatus } from '../../hooks/useChatStatus';
import { fetchPatientCard, type GetPatientResponse } from '../../services/patientCard';
import { useStatusCatalog } from '../../services/statusCatalog';
import { buildWorkQueueCaseUrl } from '../../utils/workQueueLink';
import { phaseColorOverride } from '../../utils/phaseColors';
import { accountFor, accountOwnerLabel } from '../../utils/manufacturer';
import { copyToClipboard } from '../../utils/clipboard';
import { PracticeDoctorLabel } from '../shared/PracticeDoctorLabel';
import type { ChatType } from '../../services/chat';

export interface PatientInfo {
    id: string;
    name: string;
    practiceName: string | null;
}

interface ConversationPanelProps {
    patient: PatientInfo;
    /** Which thread this row addresses; read state and assignment are per thread. */
    chatType: ChatType;
    onChatTypeChange: (chatType: ChatType) => void;
    /** Refreshes the conversations list after the panel marks a thread read or unread. */
    onReadStateChange: () => Promise<void>;
    onClose: () => void;
}

const MISSING_LOGO_CLASS = 'grayscale opacity-30';

function linkItem(
    label: string,
    href: string | null | undefined,
    missingReason?: string,
    logo?: CaseLinksLogo & { sizeClass: string }
): RowMenuItem {
    return {
        label,
        trailing: logo && (
            <img
                src={logo.logo}
                alt={logo.name}
                className={`${logo.sizeClass} flex-shrink-0 object-contain ${href ? '' : MISSING_LOGO_CLASS}`}
            />
        ),
        disabled: !href,
        disabledReason: missingReason,
        onClick: () => {
            if (href) window.open(href, '_blank', 'noopener,noreferrer');
        }
    };
}

function patientLinkItems(patientId: string, card: GetPatientResponse | null): RowMenuItem[] {
    return [
        linkItem('Open in Work Queue', buildWorkQueueCaseUrl(patientId)),
        linkItem('Open Tx plan', card?.treatment?.manufacturerLink, 'No Tx plan link', {
            ...CASE_LINKS_BRAND[caseLinksManufacturer(card?.treatment?.manufacturerSlug)],
            sizeClass: 'h-4 w-auto'
        }),
        linkItem('Open in DM', card?.monitoring?.dmLink, 'No DM link', {
            ...DM_LOGO,
            sizeClass: 'h-[18px] w-[18px]'
        })
    ];
}

interface PatientRowProps {
    patient: PatientInfo;
    card: GetPatientResponse | null;
    cardError: boolean;
}

const ConversationPatientRow: React.FC<PatientRowProps> = ({ patient, card, cardError }) => {
    const catalog = useStatusCatalog();
    const [acctCopied, setAcctCopied] = useState(false);
    const copyRevertTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (copyRevertTimerRef.current !== null) clearTimeout(copyRevertTimerRef.current);
        };
    }, []);

    // Follow the case's manufacturer: a practice may hold both accounts.
    const manufacturer = card?.treatment?.manufacturerSlug ?? null;
    const manufacturerAcct = accountFor(card?.practice, manufacturer);
    const acctLabel = accountOwnerLabel(manufacturer);
    const internalCaseStatus = card?.status?.internalCaseStatus;

    const handleCopyManufacturerAcct = async (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (!manufacturerAcct) return;
        if (!(await copyToClipboard(manufacturerAcct))) return;
        setAcctCopied(true);
        copyRevertTimerRef.current = setTimeout(() => setAcctCopied(false), 1000);
    };

    return (
        <div className="flex items-center gap-3 px-6 py-4 bg-white border-b border-gray-200 flex-shrink-0">
            <div className="w-9 h-9 flex-shrink-0">
                <Avatar
                    user={{ name: patient.name || 'N/A', email: '' }}
                    thumbnailURL={card?.profileThumbnailUrl ?? undefined}
                />
            </div>
            <div className="flex-1 min-w-0 text-left">
                <a
                    href={`/patients/${patient.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 group focus:outline-none"
                >
                    <span className="text-sm font-bold text-gray-900 truncate group-hover:underline leading-tight">
                        {patient.name}
                    </span>
                    <ChevronRightIcon className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500 transition-colors flex-shrink-0" />
                </a>
                {patient.practiceName && (
                    <div className="flex items-center gap-1 leading-tight">
                        <button
                            type="button"
                            disabled={!manufacturerAcct}
                            onClick={manufacturerAcct ? handleCopyManufacturerAcct : undefined}
                            className={
                                manufacturerAcct
                                    ? 'text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0'
                                    : 'text-gray-300 cursor-not-allowed flex-shrink-0'
                            }
                            aria-label={
                                manufacturerAcct
                                    ? `Copy ${acctLabel} account`
                                    : `No ${acctLabel} account`
                            }
                        >
                            {acctCopied ? (
                                <CheckIcon className="w-3.5 h-3.5" />
                            ) : (
                                <ClipboardIcon className="w-3.5 h-3.5" />
                            )}
                        </button>
                        <PracticeDoctorLabel
                            practiceName={patient.practiceName}
                            practiceId={card?.practice?.id}
                            format="practice-only"
                            className="text-xs truncate"
                            toneClassName="text-gray-500"
                        />
                    </div>
                )}
                {cardError && (
                    <p className="text-xs text-red-500 leading-tight">
                        Failed to load patient details
                    </p>
                )}
            </div>
            {!card && !cardError ? (
                <span
                    aria-hidden="true"
                    className="h-6 w-20 rounded-lg bg-gray-200 animate-pulse flex-shrink-0"
                />
            ) : (
                <StatusPill
                    value={internalCaseStatus ? catalog.display(internalCaseStatus) : null}
                    className="max-w-[220px] flex-shrink-0"
                    colorOverride={
                        internalCaseStatus
                            ? phaseColorOverride(catalog.meta(internalCaseStatus)?.phase)
                            : undefined
                    }
                />
            )}
            <RowMenu items={patientLinkItems(patient.id, card)} label="Patient links" />
        </div>
    );
};

interface ConversationThreadProps {
    patientId: string;
    chatType: ChatType;
    onReadStateChange: () => Promise<void>;
}

// Lives under the keyed chat provider: actions read its feed and reset per thread.
const ConversationThread: React.FC<ConversationThreadProps> = ({
    patientId,
    chatType,
    onReadStateChange
}) => {
    const actions = usePatientConversationActions(patientId, chatType, onReadStateChange);
    return (
        <>
            <div className="flex items-end gap-5 px-6 py-3 border-b border-gray-200 flex-shrink-0">
                <ConversationAssignment actions={actions} variant="panel" />
                <div className="ml-auto flex-shrink-0">
                    <ConversationReadState actions={actions} variant="panel" />
                </div>
            </div>
            <div className="flex-1 min-h-0 flex flex-col overflow-hidden bg-white">
                <ChatTab />
            </div>
        </>
    );
};

const ConversationPanel: React.FC<ConversationPanelProps> = ({
    patient,
    chatType,
    onChatTypeChange,
    onReadStateChange,
    onClose
}) => {
    const [card, setCard] = useState<GetPatientResponse | null>(null);
    const [cardError, setCardError] = useState(false);
    const { isUnread: hasUnreadExternal } = useChatStatus(patient.id, 'external');
    const { isUnread: hasUnreadInternal } = useChatStatus(patient.id, 'internal');

    useEffect(() => {
        let cancelled = false;
        setCardError(false);
        setCard(null);
        fetchPatientCard(patient.id)
            .then((data) => {
                if (!cancelled) setCard(data);
            })
            .catch(() => {
                // apiV2's interceptor already reports the failed request to Sentry.
                if (!cancelled) setCardError(true);
            });
        return () => {
            cancelled = true;
        };
    }, [patient.id]);

    // Remount per thread and version: v1 ids are Monday update ids and v2 ids
    // UUIDs, so the provider must rebuild rather than reconcile a cached feed.
    const { version: chatApiVersion } = useChatApiVersion();
    return (
        // w-2/5 alone collapses below ~1440px and cramps the header; the floor
        // keeps the panel readable while big screens keep their proportional width.
        <SlideOverModalLayout onClose={onClose} widthClass="w-2/5 min-w-[640px] max-w-[95vw]">
            {/* Outside the keyed provider so the toggle keeps focus across switches. */}
            <ConversationPatientRow patient={patient} card={card} cardError={cardError} />
            <ChatModeToggle
                value={chatType}
                onChange={onChatTypeChange}
                // Never strands a panel opened on an internal row; blocks switching to
                // internal until the card loads and shows an ortho item.
                internalDisabled={chatType === 'external' && !card?.boards?.orthoItemId}
                hasUnreadExternal={hasUnreadExternal}
                hasUnreadInternal={hasUnreadInternal}
            />
            <PatientChatProvider
                patientId={patient.id}
                key={`${chatType}-${chatApiVersion}`}
                service={getChatService(chatApiVersion)}
                chatType={chatType}
                includeActivities
            >
                <ConversationThread
                    patientId={patient.id}
                    chatType={chatType}
                    onReadStateChange={onReadStateChange}
                />
            </PatientChatProvider>
        </SlideOverModalLayout>
    );
};

export default ConversationPanel;
